// components/common/RichTextViewer.jsx
import React, { useMemo } from 'react';

const viewerStyles = `

  /* Base container styles - minimal overrides to preserve original formatting */
  .rich-text-viewer-content {
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-family: 'Aptos', 'Arial', sans-serif;
    color: #1a1a1a;
    width: 100%;
    /* Match the RichTextEditor's default line height so rendered line
       spacing is identical to what was written in the editor. */
    line-height: 1.5rem ;
  }

  /* Delta Mode: Preserves specific alignment (tabs/spaces) for academic content */
  .mode-delta { white-space: pre-wrap; }
  .mode-html { white-space: normal; }

  /* Match the RichTextEditor paragraph styles: no default margins, so
     blocks with inline margin styles (e.g. Word pasted content) keep their
     exact spacing and paragraphs without styles are not spaced apart. */
  .rich-text-viewer-content p { 
    margin: 0; 
    min-height: 1.1em;
    width: 100%;
  }

  .rich-text-viewer-content img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 1rem 0;
    border-radius: 4px;
  }

  .rich-text-viewer-content iframe {
    max-width: 100%;
    width: 100%;
    margin: 1rem 0;
  }

  .delta-inline-bg {
    padding: 1px 3px;
    border-radius: 2px;
  }

  .rich-text-viewer-content sup { 
    font-size: 0.75em; 
    vertical-align: super; 
    line-height: 0; 
  }
  
  .rich-text-viewer-content sub { 
    font-size: 0.75em; 
    vertical-align: sub; 
    line-height: 0; 
  }
  
/* Only apply default heading styles if no inline font-size/margin is set */
.rich-text-viewer-content h1:not([style*="font-size"]) { font-size: 0.75em;  font-weight: 600; margin: 1rem 0 0.5rem; }
.rich-text-viewer-content h2:not([style*="font-size"]) { font-size: 0.53em;  font-weight: 600; margin: 1rem 0 0.5rem; }
.rich-text-viewer-content h3:not([style*="font-size"]) { font-size: 0.35em; font-weight: 600; margin: 0.8rem 0 0.4rem; }
.rich-text-viewer-content h4:not([style*="font-size"]) { font-size: 0.75em;    font-weight: 600; margin: 0.6rem 0 0.3rem; }


  .rich-text-viewer-content blockquote { 
    border-left: 4px solid #3b82f6; 
    padding-left: 1rem; 
    margin: 1rem 0;
    font-style: italic; 
    color: #4b5563; 
  }

  .rich-text-viewer-content ul, 
  .rich-text-viewer-content ol { 
    padding-left: 2rem; 
    margin: 8px 0; 
  }
  
  .rich-text-viewer-content ul { list-style-type: disc; }
  .rich-text-viewer-content ol { list-style-type: decimal; }
  
  .rich-text-viewer-content li {
    margin: 0;
  }
  
  .rich-text-viewer-content table { 
    border-collapse: collapse; 
    width: 100%; 
    margin: 10px 0; 
  }
  
  .rich-text-viewer-content td, 
  .rich-text-viewer-content th { 
    border: 1px solid #ddd; 
    padding: 8px; 
    vertical-align: top; 
  }

  .rich-text-viewer-content pre {
    background: #f4f4f5;
    padding: 12px;
    border-radius: 6px;
    font-family: monospace;
    overflow-x: auto;
    margin: 8px 0;
  }

  .rich-text-viewer-content code {
    background: #f4f4f5;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
  }

  .rich-text-viewer-content a {
    color: #2563eb;
    text-decoration: underline;
  }

  .rich-text-viewer-content .multi-column {
    display: flex;
    gap: 20px;
    margin: 10px 0;
  }

  .rich-text-viewer-content .column {
    flex: 1;
    min-width: 0;
  }
.rich-text-viewer-content *:not(sup, sub, p span) {
  font-size: 1rem !important;
  line-height: 1.5rem !important;
}
  .rich-text-viewer-content table{
  max-width:100% !important;
  }

`;

const convertDeltaToHtml = (delta) => {
  try {
    let parsed = null;

    // --- NORMALIZATION LOGIC ---
    if (typeof delta === 'string') {
      const trimmed = delta.trim();
      
      if (trimmed.startsWith('[{"')) {
        // Variant 1: Raw array string "[{"attributes":...}]" -> Wrap it
        parsed = JSON.parse(`{"ops":${trimmed}}`);
      } else if (trimmed.startsWith('{"ops":')) {
        // Variant 2: Standard object string "{"ops":[{"insert":...}]}" -> Parse it
        parsed = JSON.parse(trimmed);
      } else {
        // Variant 3: Unknown string format, try parsing directly
        parsed = JSON.parse(trimmed);
      }
    } else if (Array.isArray(delta)) {
      // Variant 4: Literal Array object -> Wrap it
      parsed = { ops: delta };
    } else {
      // Variant 5: Literal Object
      parsed = delta;
    }

    if (!parsed || !parsed.ops) return String(delta);

    let html = '';
    let currentBlockOps = [];
    let listStack = [];

    const renderInline = (op) => {
      if (op.insert && typeof op.insert === 'object') {
        if (op.insert.image) return `<img src="${op.insert.image}" alt="embedded image" />`;
        if (op.insert.video) return `<iframe src="${op.insert.video}" frameBorder="0" allowFullScreen width="100%" height="315"></iframe>`;
        return '';
      }

      let text = op.insert;
      if (typeof text !== 'string' || text === '\n') return '';

      const attrs = op.attributes || {};
      const styles = [];
      const classes = [];

      if (attrs.color && attrs.color !== 'windowtext') styles.push(`color: ${attrs.color}`);
      if (attrs.background) {
        styles.push(`background-color: ${attrs.background}`);
        classes.push('delta-inline-bg');
      }
      if (attrs.size) styles.push(`font-size: ${attrs.size}`);
      if (attrs.font) styles.push(`font-family: ${attrs.font}`);
      if (attrs.lineHeight) styles.push(`line-height: ${attrs.lineHeight}`);

      let content = text;
      if (attrs.bold) content = `<strong>${content}</strong>`;
      if (attrs.italic) content = `<em>${content}</em>`;
      if (attrs.underline) content = `<u>${content}</u>`;
      if (attrs.strike) content = `<strike>${content}</strike>`;
      if (attrs.script === 'super') content = `<sup>${content}</sup>`;
      if (attrs.script === 'sub') content = `<sub>${content}</sub>`;
      
      const styleAttr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
      const classAttr = classes.length > 0 ? ` class="${classes.join(' ')}"` : '';

      if (styleAttr || classAttr) content = `<span${classAttr}${styleAttr}>${content}</span>`;
      if (attrs.link) content = `<a href="${attrs.link}" target="_blank">${content}</a>`;
      
      return content;
    };

    const closeList = () => {
      while (listStack.length > 0) {
        html += `</${listStack.pop()}>`;
      }
    };

    const flushBlock = (ops, blockAttrs = {}) => {
      let content = ops.map(op => renderInline(op)).join('');
      const styles = [];
      if (blockAttrs.align) styles.push(`text-align: ${blockAttrs.align}`);
      if (blockAttrs.lineHeight) styles.push(`line-height: ${blockAttrs.lineHeight}`);
      if (blockAttrs.marginTop) styles.push(`margin-top: ${blockAttrs.marginTop}`);
      if (blockAttrs.marginBottom) styles.push(`margin-bottom: ${blockAttrs.marginBottom}`);
      const styleAttr = styles.length > 0 ? ` style="${styles.join('; ')}"` : '';

      if (blockAttrs.header) {
        closeList();
        html += `<h${blockAttrs.header}${styleAttr}>${content || '&nbsp;'}</h${blockAttrs.header}>`;
      } else if (blockAttrs.blockquote) {
        closeList();
        html += `<blockquote${styleAttr}>${content || '&nbsp;'}</blockquote>`;
      } else if (blockAttrs.list) {
        const tag = blockAttrs.list === 'ordered' ? 'ol' : 'ul';
        if (listStack.length === 0 || listStack[listStack.length - 1] !== tag) {
          closeList();
          html += `<${tag}>`;
          listStack.push(tag);
        }
        html += `<li${styleAttr}>${content || '&nbsp;'}</li>`;
      } else {
        closeList();
        html += `<p${styleAttr}>${content || '&nbsp;'}</p>`;
      }
    };

    parsed.ops.forEach(op => {
      if (typeof op.insert === 'string') {
        const parts = op.insert.split('\n');
        parts.forEach((part, index) => {
          if (index < parts.length - 1) {
            if (part) currentBlockOps.push({ ...op, insert: part });
            flushBlock(currentBlockOps, op.attributes || {});
            currentBlockOps = [];
          } else if (part) {
            currentBlockOps.push({ ...op, insert: part });
          }
        });
      } else {
        currentBlockOps.push(op);
      }
    });

    if (currentBlockOps.length > 0) flushBlock(currentBlockOps, {});
    closeList();

    return html;
  } catch (e) {
    console.error("Delta Parsing Error:", e);
    return String(delta);
  }
};

/**
 * Extract a canonical hex color from Word's named-color values, matching the
 * same mapping the RichTextEditor uses on paste.
 */
const extractExactColor = (colorValue) => {
  if (!colorValue) return null;
  if (colorValue.match(/^#[0-9A-Fa-f]{3,6}$/)) return colorValue;

  const colorMap = {
    'darkblue': '#00008b', 'navy': '#000080', 'navyblue': '#000080', 'dark navy': '#000080',
    'blue': '#0000ff', 'black': '#000000', 'white': '#ffffff', 'red': '#ff0000',
    'green': '#00ff00', 'yellow': '#ffff00', 'gray': '#808080', 'purple': '#800080', 'darkpurple': '#4b0082'
  };

  const lowerColor = String(colorValue).toLowerCase().trim();
  return colorMap[lowerColor] || colorValue;
};

const isDarkColor = (color) => {
  if (!color) return false;
  const c = String(color).toLowerCase();
  return c.includes('blue') || c.includes('navy') || c.includes('#000080') || c.includes('#00008b') || c.includes('black') || c.includes('purple');
};

/**
 * Normalize HTML (especially Microsoft Word pasted content) so the viewer
 * renders it exactly as the RichTextEditor displays the same content.
 *
 * This mirrors the editor's cleanWordFormatting() logic: strip Word
 * conditional comments / office cruft, rebuild a whitelist of layout styles
 * (color, background, alignment, margins, font-size, line-height), resolve
 * named colors to hex, remove stray class/lang/bgcolor/align attributes, and
 * normalize tables. It additionally flattens nested block elements that Word
 * produces (e.g. <h1> wrapping <div> wrapping <p>) so text is not rendered as a
 * huge heading.
 */
const sanitizeHtml = (html) => {
  if (!html) return '';

  try {
    // Table placeholders so their inner content isn't mutated by the element pass.
    const tableMatches = [];
    let processedHtml = String(html);
    const tableRegex = /<table[\s\S]*?<\/table>/gi;
    let match, tableIndex = 0;
    while ((match = tableRegex.exec(html)) !== null) {
      tableMatches.push(match[0]);
      processedHtml = processedHtml.replace(match[0], `__TABLE_PLACEHOLDER_${tableIndex}__`);
      tableIndex++;
    }

    // Strip Word/office markup and conditional comments.
    const clean = processedHtml
      .replace(/<o:p>[\s\S]*?<\/o:p>/g, '&nbsp;')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/<(meta|link|style|title)[\s\S]*?>[\s\S]*?<\/\1>/gi, '')
      .replace(/<\!\[endif\]>/gi, '')
      .replace(/<\!\[if[^>]*>[\s\S]*?<\!\[endif\]>/gi, '');

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = clean || '';

    const blockTags = ['P', 'DIV', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI'];

// A heading in the source HTML is "really" a heading only if:
//   - it has no inline font-size overriding it, OR
//   - its inline font-size is >= the CSS size for that heading (i.e., it's
//     genuinely larger text), AND
//   - it doesn't wrap block-level children, AND
//   - it's not nested inside another heading, AND
//   - its text is short and heading-like (not a paragraph of body copy)
//
// Word and LibreOffice love to wrap body content in <h1>/<h2>/<h3> because
// they map "Outline level 1" to those tags. Anything that looks like body
// copy should be unwrapped to a <div> to preserve its own styling without
// inheriting heading CSS.
const HEADING_CSS_SIZE = { H1: 1.8, H2: 1.5, H3: 1.17, H4: 1, H5: 1, H6: 1 };

const headingFontSizeInEm = (h) => {
  const style = h.getAttribute('style') || '';
  const match = style.match(/font-size:\s*([\d.]+)\s*(pt|px|em|rem|%)/i);
  if (!match) return null;

  const value = parseFloat(match[1]);
  const unit = match[2].toLowerCase();

  // Assume a base of 16px / 12pt for conversion. That's close enough to
  // make the "is this really a heading?" decision.
  if (unit === 'pt') return value / 12;
  if (unit === 'px') return value / 16;
  if (unit === 'em' || unit === 'rem') return value;
  if (unit === '%') return value / 100;
  return null;
};

const isBodyLikeHeading = (h) => {
  const hasBlockChild = Array.from(h.children).some((c) =>
    blockTags.includes(c.tagName)
  );
  const nestedInHeading =
    h.parentElement && /^H[1-6]$/.test(h.parentElement.tagName);
  const text = (h.textContent || '').replace(/\u00a0/g, ' ').trim();

  // Empty or filler
  if (text.length === 0) return true;

  // Wraps block content → not a real heading
  if (hasBlockChild || nestedInHeading) return true;

  // Inline font-size smaller than the heading's natural CSS size → treat as body
  const inlineSize = headingFontSizeInEm(h);
  if (inlineSize != null) {
    const cssSize = HEADING_CSS_SIZE[h.tagName] || 1;
    // Anything up to 1.3× the CSS default that would apply is "just styled
    // text," not an intentional heading. Headings authored deliberately in
    // an editor usually end up at ~1.5–2× the base size.
    if (inlineSize < cssSize * 1.3) return true;
  }
  // Inside isBodyLikeHeading, after the inlineSize check:
const looksLikeSectionHeader =
  inlineSize != null &&
  inlineSize >= 1.1 &&
  /<b>|<strong>/i.test(h.innerHTML) &&
  text.length < 40;

if (looksLikeSectionHeader) return false; // keep it as a heading

  // Long inline-only run → almost certainly body copy
  if (text.length > 100) return true;

  return false;
};

    // Iterative, because flattening an outer heading can expose inner headings
    // that then also need flattening (h1 > div > h1 > …).
let remainingHeadings = tempDiv.querySelectorAll('h1,h2,h3,h4,h5,h6');
let safety = 0;
while (remainingHeadings.length > 0 && safety < 20) {
  let flattened = false;
  remainingHeadings.forEach((h) => {
    if (!h.parentElement || !isBodyLikeHeading(h)) return;

    // Prefer <p> when the heading is inline-only text (paragraph-ish),
    // <div> when it has block children (container-ish).
    const hasBlockChild = Array.from(h.children).some((c) =>
      blockTags.includes(c.tagName)
    );
    const replacement = document.createElement(hasBlockChild ? 'div' : 'p');

    // Preserve all inline styles so font-family/size/color survive
    const styleAttr = h.getAttribute('style');
    if (styleAttr) replacement.setAttribute('style', styleAttr);

    while (h.firstChild) replacement.appendChild(h.firstChild);
    h.replaceWith(replacement);
    flattened = true;
  });

  if (!flattened) break;
  remainingHeadings = tempDiv.querySelectorAll('h1,h2,h3,h4,h5,h6');
  safety++;
}
    // Collapse a div that exists purely to wrap another block, transferring its
    // layout styles up to the child to keep alignment/margins.
    tempDiv.querySelectorAll('div').forEach((d) => {
      const children = Array.from(d.children);
      if (children.length === 1 && blockTags.includes(children[0].tagName)) {
        const child = children[0];
        const styleAttr = d.getAttribute('style');
        if (styleAttr) {
          const inherited = styleAttr.split(';')
            .map((s) => s.trim())
            .filter((s) => /^(text-align|margin|padding|line-height|font-size|font-family|color|background):/i.test(s));
          if (inherited.length) {
            const existing = child.getAttribute('style') || '';
            child.setAttribute('style', (inherited.join('; ') + '; ' + existing).trim().replace(/;+\s*$/, ''));
          }
        }
        d.replaceWith(child);
      }
    });

    // Whitelist style rebuild — mirrors the editor's cleanWordFormatting pass.
    tempDiv.querySelectorAll('*').forEach((el) => {
      const style = el.getAttribute('style') || '';

      const bgMatch = style.match(/(?:background-color|background|mso-shading|mso-highlight):\s*([^;]+)/i);
      if (bgMatch) {
        let color = bgMatch[1].trim().replace(/['"]/g, '');
        const exactColor = extractExactColor(color);
        if (exactColor) {
          el.style.backgroundColor = exactColor;
          el.style.setProperty('background-color', exactColor, 'important');
          if (isDarkColor(exactColor)) el.style.setProperty('color', '#ffffff', 'important');
        }
      }

      if (el.hasAttribute('bgcolor')) {
        const exactColor = extractExactColor(el.getAttribute('bgcolor'));
        if (exactColor) {
          el.style.backgroundColor = exactColor;
          el.style.setProperty('background-color', exactColor, 'important');
          if (isDarkColor(exactColor)) el.style.setProperty('color', '#ffffff', 'important');
        }
        el.removeAttribute('bgcolor');
      }

      const colorMatch = style.match(/color:\s*([^;]+)/i);
      if (colorMatch) {
        const exactColor = extractExactColor(colorMatch[1]);
        if (exactColor) el.style.setProperty('color', exactColor, 'important');
      }

      const alignMatch = style.match(/text-align:\s*([^;]+)/i);
      if (alignMatch) el.style.textAlign = alignMatch[1];

      const indentMatch = style.match(/margin-left:\s*([^;]+)/i);
      if (indentMatch) el.style.marginLeft = indentMatch[1];

      const fontSizeMatch = style.match(/font-size:\s*([^;]+)/i);
      if (fontSizeMatch) el.style.fontSize = fontSizeMatch[1];

      const lineHeightMatch = style.match(/line-height:\s*([^;]+)/i);
      if (lineHeightMatch) el.style.lineHeight = lineHeightMatch[1];

      const marginTopMatch = style.match(/margin-top:\s*([^;]+)/i);
      if (marginTopMatch) el.style.marginTop = marginTopMatch[1];

      const marginBottomMatch = style.match(/margin-bottom:\s*([^;]+)/i);
      if (marginBottomMatch) el.style.marginBottom = marginBottomMatch[1];

      // Drop <font> tags, keeping face/size/color via spans.
  if (el.tagName === 'FONT') {
  const span = document.createElement('span');
  const spec = [];
  const face = el.getAttribute('face');
  const color = el.getAttribute('color');

  // Only preserve face when it's a "meaningful" choice, not just Word
  // re-asserting the default. Extend this list to taste.
  const BORING_FACES = ['arial', 'helvetica', 'calibri', 'times new roman', 'aptos'];
  if (face && !BORING_FACES.includes(face.toLowerCase())) {
    spec.push(`font-family:${face}`);
  }
  if (color) spec.push(`color:${color}`);

  if (spec.length) span.setAttribute('style', spec.join('; '));
  while (el.firstChild) span.appendChild(el.firstChild);
  el.replaceWith(span);
  return;
}

      el.removeAttribute('class');
      el.removeAttribute('lang');
    });

    // Rebuild tables with normalized borders/alignment like the editor.
    const processedTables = tableMatches.map((tableHtml) => {
      const tableDiv = document.createElement('div');
      tableDiv.innerHTML = tableHtml;
      const table = tableDiv.querySelector('table');
      if (table) {
        if (table.hasAttribute('bgcolor')) {
          const exactColor = extractExactColor(table.getAttribute('bgcolor'));
          if (exactColor) table.style.setProperty('background-color', exactColor, 'important');
          table.removeAttribute('bgcolor');
        }
        const cells = table.querySelectorAll('td, th');
        cells.forEach((cell) => {
          if (cell.hasAttribute('bgcolor')) {
            const exactColor = extractExactColor(cell.getAttribute('bgcolor'));
            if (exactColor) cell.style.setProperty('background-color', exactColor, 'important');
            cell.removeAttribute('bgcolor');
          }
          if (cell.hasAttribute('align')) {
            cell.style.textAlign = cell.getAttribute('align');
            cell.removeAttribute('align');
          }
          if (!cell.style.border) cell.style.border = '1px solid #ddd';
        });
        return table.outerHTML;
      }
      return tableHtml;
    });

    let finalHtml = tempDiv.innerHTML;
    processedTables.forEach((tableHtml, idx) => {
      finalHtml = finalHtml.replace(`__TABLE_PLACEHOLDER_${idx}__`, tableHtml);
    });
    finalHtml = finalHtml.replace(/__TABLE_PLACEHOLDER_\d+__/g, '');

    return finalHtml.replace(/>\s+</g, '><').replace(/(<p>&nbsp;<\/p>){2,}/gi, '<p>&nbsp;</p>').trim();
  } catch (e) {
    return String(html);
  }
};

const RichTextViewer = ({ content = '', className = '', maxHeight = 'none', minHeight = 'auto', padding = '0px' }) => {
  
  const { processedContent, isDelta } = useMemo(() => {
    if (!content) return { processedContent: '', isDelta: false };
    
    const trimmed = typeof content === 'string' ? content.trim() : '';
    
    // Detection logic: is it a Quill format?
    const isDeltaFormat = (typeof content === 'object' && (content.ops || Array.isArray(content))) ||
                          trimmed.startsWith('[{"') || 
                          trimmed.startsWith('{"ops":');

    if (isDeltaFormat) {
      return { processedContent: convertDeltaToHtml(content), isDelta: true };
    }
    
    // Standard HTML fallback (for content from RichTextEditor)
    // Return the HTML exactly as-is to preserve all inline styles
    return { processedContent: sanitizeHtml(content), isDelta: false };
  }, [content]);

  if (!content || (typeof content === 'string' && content.trim() === '')) {
    return <div className="text-gray-400 italic text-sm">No content provided.</div>;
  }

  return (
    <div className={`rich-text-viewer-wrapper ${className} w-full`}>
      <style>{viewerStyles}</style>
      <div 
        className={`rich-text-viewer-content ${isDelta ? 'mode-delta' : 'mode-html'}`}
        style={{ 
          maxHeight, 
          minHeight, 
          padding, 
          overflowY: maxHeight !== 'none' ? 'auto' : 'visible'
        }}
        dangerouslySetInnerHTML={{ __html: processedContent }}
      />
    </div>
  );
};

export default RichTextViewer;